<template>
  <EquipmentRow data-cy="bolt-on-discharge-row-equipment-row">
    <template #left></template>

    <template #default="{ toggle }">
      <AddRemoveButtons
        :hide-add-below="true"
        :hide-remove="!isHps"
        :disable-remove="!selectedBoltOnDischargeId"
        data-cy="bolt-on-discharge-row-add-remove-buttons"
        @remove="handleBoltOnDischargeRemove"
      >
        <template #middle-content>
          <EquipmentPanel
            class="flex-grow-1"
            :disable-toggle="!isHps"
            data-cy="bolt-on-discharge-row-equipment-panel"
            @toggle="toggle"
          >
            <template #header>Bolt On Discharge</template>
            <template v-if="selectedBoltOnDischarge" #trigger>
              {{ computeBoltOnDischargeLabel(selectedBoltOnDischarge) }}
            </template>
          </EquipmentPanel>
        </template>
      </AddRemoveButtons>
    </template>
    <template #expand="{ toggle }">
      <div v-if="isHps" class="col-12 text-center">
        <HpsBoltOnDischargeSearchView
          :is-admin="false"
          data-cy="bolt-on-discharge-row-search-view"
          @select="handleBoltOnDischargeSelect($event, toggle)"
        />
      </div>
    </template>
    <template #right>
      <div
        v-if="!stack"
        class="flex align-items-center"
        style="gap: 1em"
        data-cy="bolt-on-discharge-row-right"
      >
        <BlankMeasurement
          v-if="isLengthUnknown"
          label="Length"
          data-cy="bolt-on-discharge-row-length-blank"
        />
        <ConverterInput
          v-else
          label="Length"
          :model-value="selectedBoltOnDischarge?.length_inches || 0"
          :base-unit="LengthUnits.Inches"
          :target-unit="getLargeLengthUnit('Length.convert.Inches')"
          :model="LargeLengthUnits.options()"
          mode="convert"
          data-cy="bolt-on-discharge-row-length-converter"
        />
        <BlankMeasurement
          v-if="isWeightUnknown"
          label="Weight"
          data-cy="bolt-on-discharge-row-weight-blank"
        />
        <ConverterInput
          v-else
          label="Weight"
          :model-value="selectedBoltOnDischarge?.weight_pounds || 0"
          :base-unit="MassUnits.Pounds"
          :model="MassUnits.options()"
          mode="convert"
          data-cy="bolt-on-discharge-row-weight-converter"
        />
      </div>
    </template>
  </EquipmentRow>
  <!-- Length and Weight below EquipmentRow in compact mode -->
  <div
    v-if="stack"
    class="compact-bolt-on-discharge-dimensions flex gap-4"
    data-cy="bolt-on-discharge-row-compact-dimensions"
  >
    <BlankMeasurement v-if="isLengthUnknown" label="Length" class="flex-1" />
    <ConverterInput
      v-else
      label="Length"
      :model-value="selectedBoltOnDischarge?.length_inches || 0"
      :base-unit="LengthUnits.Inches"
      :target-unit="getLargeLengthUnit('Length.convert.Inches')"
      :model="LargeLengthUnits.options()"
      mode="convert"
      class="flex-1"
    />
    <BlankMeasurement v-if="isWeightUnknown" label="Weight" class="flex-1" />
    <ConverterInput
      v-else
      label="Weight"
      :model-value="selectedBoltOnDischarge?.weight_pounds || 0"
      :base-unit="MassUnits.Pounds"
      :model="MassUnits.options()"
      mode="convert"
      class="flex-1"
    />
  </div>
</template>

<script setup lang="ts">
import ConverterInput from "@/components/ConverterInput.vue";
import BlankMeasurement from "@/components/equipment/bolt_on_discharges/BlankMeasurement.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import HpsBoltOnDischargeSearchView from "@/components/equipment/hps_bolt_on_discharges/HpsBoltOnDischargeSearchView.vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { injectStrict } from "@/utils/utils";
import { computed } from "vue";
import {
  equipmentFormKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import {
  computeBoltOnDischargeLabel,
  isBoltOnDischargeMeasurementUnknown
} from "@/models/equipment/bolt_on_discharges/properties";
import type { Bolt_On_Discharges } from "gql/api";
import type { StackLocation } from "@/components/equipment/d";

/**
 * The ESP selection is derived where the scenario is provided, by
 * useAutomaticBoltOnDischarge, so that every screen reads the same value. This
 * row displays that value and handles the manual HPS selection.
 */
defineProps<{
  stack?: StackLocation;
  isHps?: boolean;
}>();

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

const selectedBoltOnDischarge = computed(
  () => equipment.value?.r_bolt_on_discharge
);

const selectedBoltOnDischargeId = computed(
  () => equipment.value?.bolt_on_discharge_id
);

const isLengthUnknown = computed(() =>
  isBoltOnDischargeMeasurementUnknown(
    selectedBoltOnDischarge.value,
    "length_inches"
  )
);

const isWeightUnknown = computed(() =>
  isBoltOnDischargeMeasurementUnknown(
    selectedBoltOnDischarge.value,
    "weight_pounds"
  )
);

function writeSelection(boltOnDischarge: Bolt_On_Discharges | null) {
  equipmentForm.update({
    r_bolt_on_discharge: boltOnDischarge,
    bolt_on_discharge_id: boltOnDischarge?.id ?? null
  } as Partial<typeof equipmentForm.data.value>);
}

function handleBoltOnDischargeSelect(
  boltOnDischarge: Bolt_On_Discharges,
  toggle: () => void
) {
  toggle();
  writeSelection(boltOnDischarge);
}

function handleBoltOnDischargeRemove() {
  writeSelection(null);
}
</script>
